import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, FileText, ArrowRight, UserCheck, Pill, Package } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function VerifyRx() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null, throttled: null });
  const [dispenseOpen, setDispenseOpen] = useState(false);
  const [form, setForm] = useState({ pharmacy_name: "", pharmacist_name: "", confirm: false });
  const [saving, setSaving] = useState(false);

  const fetchVerify = useCallback(async () => {
    try {
      const { data } = await api.get(`/clinic-growth/prescriptions/${token}/verify`);
      setState({ loading: false, data, error: null, throttled: null });
    } catch (e) {
      // Distinguish throttling from a genuine 'not found' so patients and
      // pharmacists aren't misled into thinking the Rx is invalid.
      const status = e?.response?.status;
      if (status === 429) {
        const retry = Number(e?.response?.headers?.["retry-after"]) || 30;
        setState({
          loading: false, data: null, error: null,
          throttled: { retry_after: retry, detail: e?.response?.data?.detail || "Too many requests." },
        });
      } else {
        setState({ loading: false, data: null, error: formatApiError(e), throttled: null });
      }
    }
  }, [token]);

  useEffect(() => { fetchVerify(); }, [fetchVerify]);

  const submitDispense = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/clinic-growth/prescriptions/${token}/dispense`, form,
      );
      toast.success(`Dispense logged at ${data.dispense.pharmacy_name}`);
      setDispenseOpen(false);
      setForm({ pharmacy_name: "", pharmacist_name: "", confirm: false });
      fetchVerify();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Prescription verification</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {state.loading && (
          <div className="rounded-lg border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="verify-loading">
            Verifying…
          </div>
        )}

        {state.error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-8" data-testid="verify-not-found">
            <div className="flex items-center gap-2 text-rose-800">
              <ShieldAlert size={18} />
              <div className="font-serif text-2xl">Prescription not found</div>
            </div>
            <p className="mt-2 text-sm text-rose-900/80">
              This link is invalid, expired, or the prescription has been revoked.
              If a doctor shared it with you, double-check that you opened the full
              URL from your WhatsApp message.
            </p>
            <div className="mt-4">
              <Link to="/" className="text-sm text-rose-900 underline">Return to Arogya Pramana</Link>
            </div>
          </div>
        )}

        {state.throttled && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-8" data-testid="verify-throttled">
            <div className="flex items-center gap-2 text-amber-900">
              <ShieldAlert size={18} />
              <div className="font-serif text-2xl">Too many verification attempts</div>
            </div>
            <p className="mt-2 text-sm text-amber-900/90">
              You've refreshed this page a lot in the last minute. The prescription
              is still valid — this throttle just slows down automated scanning.
              Please wait <b>{state.throttled.retry_after}s</b> and try again.
            </p>
            <div className="mt-4">
              <Button onClick={fetchVerify} variant="outline" data-testid="verify-throttle-retry">
                Try again
              </Button>
            </div>
          </div>
        )}

        {state.data && (
          <AuthenticCard
            d={state.data}
            onOpenDispense={() => setDispenseOpen(true)}
          />
        )}

        <div className="mt-8 text-[11px] text-muted-foreground">
          Verification is read-only. Diagnoses and prescriptions are authored by
          licensed Registered Medical Practitioners (RMPs). Information is retained
          by Arogya Pramana under the Telemedicine Practice Guidelines (2020) and
          the DPDP Act, 2023.
        </div>
      </div>

      {/* Dispense dialog */}
      <Dialog open={dispenseOpen} onOpenChange={setDispenseOpen}>
        <DialogContent data-testid="dispense-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={16} /> Mark prescription as dispensed
            </DialogTitle>
            <DialogDescription>
              Pharmacists only — this stamps a tamper-evident entry on the patient's
              verification page so they know their medicines have been handed over.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <div>
              <Label>Pharmacy name <span className="text-rose-700">*</span></Label>
              <Input value={form.pharmacy_name}
                     onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })}
                     placeholder="e.g. Apollo Pharmacy, Bandra West"
                     data-testid="dispense-pharmacy" />
            </div>
            <div>
              <Label>Pharmacist name <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input value={form.pharmacist_name}
                     onChange={(e) => setForm({ ...form, pharmacist_name: e.target.value })}
                     placeholder="e.g. Reena S."
                     data-testid="dispense-pharmacist" />
            </div>
            <div className="flex items-start gap-2 rounded-md bg-primary/5 border border-primary/15 p-3">
              <Checkbox checked={form.confirm}
                        onCheckedChange={(v) => setForm({ ...form, confirm: !!v })}
                        data-testid="dispense-confirm" />
              <Label className="text-xs text-primary leading-snug">
                I confirm I have dispensed the above medicines to the patient and
                take professional responsibility for this attestation. Entry is
                append-only and visible to the patient.
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setDispenseOpen(false)}>Cancel</Button>
            <Button onClick={submitDispense}
                    disabled={saving || !form.pharmacy_name.trim() || !form.confirm}
                    data-testid="dispense-submit">
              {saving ? "Logging…" : "Log dispense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuthenticCard({ d, onOpenDispense }) {
  const signedDate = d.signed_at ? new Date(d.signed_at).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";
  const canDispense = (d.dispenses_remaining ?? 0) > 0;
  return (
    <div className="space-y-5" data-testid="verify-authentic">
      {/* Trust seal */}
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
            <ShieldCheck size={22} className="text-emerald-800" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800">Trust seal</div>
            <div className="font-serif text-2xl text-emerald-900">Authentic prescription</div>
          </div>
          <Badge className="ml-auto bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="verify-badge-authentic">✓ Verified</Badge>
        </div>
        <p className="mt-4 text-sm text-emerald-900/90">
          This prescription was digitally signed by a verified doctor on <b>{signedDate}</b> and
          dispatched to <b>{d.patient_name_masked}</b> via Arogya Pramana ClinicGrowth / CareOS.
        </p>
      </div>

      {/* Doctor card */}
      {d.doctor && (
        <div className="rounded-lg border border-border bg-white p-6" data-testid="verify-doctor-card">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Signed by</div>
          <div className="mt-3 flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/8 text-primary flex items-center justify-center font-serif text-xl">
              {d.doctor.picture ? (
                <img src={d.doctor.picture} alt="" className="h-full w-full rounded-full object-cover" />
              ) : (
                (d.doctor.name || "D").trim().charAt(0)
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-serif text-xl text-primary">{d.doctor.name}</div>
                {d.doctor.verified && (
                  <Badge className="bg-primary/10 text-primary border-primary/20"><UserCheck size={10} className="mr-1" /> Verified RMP</Badge>
                )}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {d.doctor.specialty || "Registered Medical Practitioner"}
                {d.doctor.organization ? ` · ${d.doctor.organization}` : ""}
              </div>
              {d.doctor.rmp_number && (
                <div className="mt-1 text-xs text-muted-foreground">
                  RMP registration: <span className="font-mono">{d.doctor.rmp_number}</span>
                </div>
              )}
              {d.doctor.profile_url && (
                <Link to={d.doctor.profile_url}
                      className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      data-testid="verify-doctor-profile-link">
                  View public profile <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rx body */}
      <div className="rounded-lg border border-border bg-white p-6">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Prescription</div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Approved diagnosis</div>
          <div className="mt-1 text-base text-primary" data-testid="verify-diagnosis">{d.approved_diagnosis || "—"}</div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Treatment plan</div>
          <pre className="mt-1 whitespace-pre-wrap font-mono text-sm text-primary" data-testid="verify-plan">{d.treatment_plan || "—"}</pre>
        </div>
        {d.pdf_url && (
          <div className="mt-5 flex items-center gap-2">
            <Button asChild variant="outline" data-testid="verify-pdf-link">
              <a href={d.pdf_url} target="_blank" rel="noopener noreferrer">
                <FileText size={14} className="mr-1" /> Download signed PDF
              </a>
            </Button>
            <span className="text-[11px] text-muted-foreground">Opens in a new tab · ~15 KB</span>
          </div>
        )}
      </div>

      {/* Dispense log */}
      <div className="rounded-lg border border-border bg-white p-6" data-testid="verify-dispenses">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Dispensing log</div>
            <div className="mt-1 font-serif text-xl text-primary">Pharmacy attestations</div>
          </div>
          <Button
            onClick={onOpenDispense}
            disabled={!canDispense}
            variant={canDispense ? "default" : "outline"}
            data-testid="verify-open-dispense"
          >
            <Pill size={14} className="mr-1" />
            {canDispense ? "Mark as dispensed" : "Dispense log full"}
          </Button>
        </div>
        {(d.dispenses || []).length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed border-border bg-muted/10 p-5 text-center text-xs text-muted-foreground">
            Not dispensed yet. When a pharmacist marks this Rx dispensed, you'll see
            the pharmacy name and timestamp right here.
          </div>
        ) : (
          <ol className="mt-4 space-y-3">
            {d.dispenses.map((entry, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`verify-dispense-row-${i}`}>
                <div className="h-8 w-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-800 shrink-0">
                  <Pill size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-primary">
                    Dispensed at <b>{entry.pharmacy_name}</b>
                    {entry.pharmacist_name ? <span className="text-muted-foreground"> · by {entry.pharmacist_name}</span> : null}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(entry.at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
        <div className="mt-3 text-[10px] text-muted-foreground">
          Up to 3 dispenses per Rx. IPs are one-way hashed for abuse detection but not stored or displayed.
        </div>
      </div>
    </div>
  );
}

